/* Copyright (c) 2026 Xperi. All Rights Reserved. */

export { default as myHubReducer } from "./myHubSlice";
export {
  // Focus setters
  focusMyHubFilterChip,
  focusMyHubSortChip,
  focusMyHubCarouselTile,
  focusMyHubSavedItem,
  focusMyHubBulkAction,
  focusMyHubSeriesAction,
  focusMyHubSeriesItem,
  // Scheduled-tab drill-in seeding
  seedMyHubScheduledSingle,
  // D-pad commit actions
  applyMyHubFilter,
  applyMyHubSort,
  // Filter/tab strip fetching
  fetchMyHubFilters,
  // Carousel fetching
  fetchMyHubCarousels,
  loadMoreMyHubCarousels,
  canLoadMoreMyHubCarousels,
  loadMoreMyHubTiles,
  fetchMyHubCarouselTilePage,
  // Saved / Deleted flat-list fetching
  fetchMyHubSavedList,
  loadMoreMyHubSavedList,
  // Manage-series folder fetching
  fetchMyHubSeriesDetail,
  // Recording quota
  setMyHubRecordingQuota,
  fetchMyHubRecordingQuota,
  toRecordingQuota,
  // Screen lifecycle
  resetMyHubFocus,
  resetMyHubContent,
  resetMyHubState,
  persistMyHubState,
  setMyHubExiting,
  setMyHubScrollInstantly,
  setCarouselRefreshPending,
  leaveMyHubScreen,
  resetMyHubSort,
  leaveMyHubSeriesScreen,
  exitMyHubSeriesScreen,
  refreshMyHubAfterAction,
  // Carousel sync
  setCarouselItemCounts,
  // D-pad navigation actions
  navigateMyHubLeft,
  navigateMyHubRight,
  navigateMyHubUp,
  navigateMyHubDown,
  navigateMyHubSeriesLeft,
  navigateMyHubSeriesRight,
  navigateMyHubSeriesUp,
  navigateMyHubSeriesDown,
  // Selectors
  selectMyHubSelectedFilter,
  selectMyHubSelectedSort,
  selectMyHubFilterOptions,
  selectIsLoadingMyHubFilters,
  selectMyHubFiltersError,
  selectMyHubCarousels,
  selectMyHubCarouselsPagination,
  selectIsLoadingMyHubCarousels,
  selectIsRefreshingMyHubCarousels,
  selectMyHubCarouselsError,
  selectMyHubTilePagination,
  selectAllMyHubTilePagination,
  selectMyHubRecordingQuota,
  selectMyHubFocusRegion,
  selectMyHubFocusedFilterIndex,
  selectMyHubFocusedSortIndex,
  selectMyHubFocusedCarouselIndex,
  selectMyHubFocusedItemIndex,
  selectMyHubCarouselScrollPositions,
  selectMyHubScrollInstantly,
  selectMyHubSavedItems,
  selectMyHubSavedSummary,
  selectIsLoadingMyHubSavedList,
  selectIsLoadingMoreMyHubSavedList,
  selectMyHubSavedListError,
  selectMyHubSavedListCarouselId,
  selectMyHubSavedListResolvedCount,
  selectMyHubFocusedSavedIndex,
  selectMyHubFocusedSavedActionIndex,
  selectMyHubFocusedSavedAction,
  selectMyHubSavedBulkAction,
  selectMyHubSeriesItems,
  selectMyHubSeriesSummary,
  selectIsLoadingMyHubSeries,
  selectMyHubSeriesError,
  selectMyHubFocusedSeriesActionIndex,
  selectMyHubSeriesFolderActions,
  selectMyHubFocusedSeriesAction,
  // Analytics
  myHubTabAnalyticsLogger,
  manageSeriesAnalyticsLogger,
  captureMyHubEntryScreen
} from "./myHubSlice";
export {
  MY_HUB_CONTENT_HIGHLIGHT_DWELL_MS,
  MY_HUB_SAVED_LIST_NAMES,
  resolveMyHubSavedListName,
  MY_HUB_SCREEN_ID,
  MY_HUB_SCREEN_NAME
} from "./myHub.types";
export type { TMyHubFocusedSavedAction } from "./myHubSlice";
export { MY_HUB_SORT_OPTIONS, isMyHubFlatListFilter } from "./myHubFilters";
export type { TMyHubFilterMeta, TMyHubSortMeta } from "./myHubFilters";
export {
  MyHubFilterId,
  MyHubSortId,
  MyHubFocusRegion,
  MY_HUB_ORDER_BY
} from "./myHub.types";
export type { TMyHubOrderBy } from "./myHub.types";
export type {
  MyHubState,
  TMyHubRecordingQuota,
  TMyHubFiltersState,
  TMyHubCarouselData,
  TMyHubCarouselPaginationState,
  TMyHubSavedItem,
  TMyHubSavedListSummary,
  TMyHubSavedListState,
  TMyHubSeriesSummary,
  TMyHubSeriesState
} from "./myHub.types";
export type { TCarouselItem } from "../../model/types";
export { OfferType } from "../../model/enums";
export { mapMyHubItemToCarouselItem, mapMyHubCarousel } from "./myHubMappers";
export {
  mapMyHubSavedItem,
  mapMyHubSavedList,
  mapMyHubSavedSummary,
  composeSavedHeadline,
  composeSavedSummaryText,
  composeSavedBulkActionLabel,
  composeSavedEmptyMessage,
  formatSavedDate,
  formatSavedDuration,
  interpolate
} from "./myHubSavedMappers";
export {
  mapMyHubSeriesItem,
  mapMyHubFolderDetail,
  mapMyHubSeriesSummary,
  composeSeriesHeadline,
  composeSeriesAiringLabel,
  composeSeriesSubtitle,
  toRenderableFolderActions,
  formatAiringTimeRange,
  mapScheduledSingleRecording
} from "./myHubSeriesMappers";
export type { TMyHubFolderActionButton } from "./myHubSeriesMappers";
export { dismissMyHubScreenErrorDialog } from "./myHubScreenErrorDialog";
// The MyHub service's own contracts — the response envelopes, the item variants
// and the affordance enums — are Domain, not ViewModel, and are exported by
// `model/types` (see TMyHubDto.ts). They are deliberately NOT re-exported here:
// a second path to the same type is what let views reach into this module's
// internals in the first place, and re-exporting them would drag this slice —
// and the interactor graph behind it — into every leaf that only wanted an enum.
